type Align = 'left' | 'right' | 'center' | 'justify';

enum HeadingEnum {
  HEADING_ONE = 'heading-one',
  HEADING_TWO = 'heading-two',
  HEADING_THREE = 'heading-three',
  HEADING_FOUR = 'heading-four',
  HEADING_FIVE = 'heading-five',
  HEADING_SIX = 'heading-six',
}

type Heading =
  | 'heading-one'
  | 'heading-two'
  | 'heading-three'
  | 'heading-four'
  | 'heading-five'
  | 'heading-six';

interface ITextNode {
  text?: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  underline?: boolean;
  textColor?: string;
  fillColor?: string;
  children?: ITextNode[];
  type?: 'list-item' | 'link';
  align?: Align;
  href?: string;
}

export interface IElement {
  type?: 'paragraph' | 'bulleted-list' | 'numbered-list' | Heading | 'link';
  children?: ITextNode[];
  align?: Align;
}

interface IHeadingStyleMapper {
  fontSize: string;
  lineHeight: number;
}

export const RICH_TEXT_SAMPLE2: IElement[] = [
  {
    type: 'heading-three',
    align: 'left',
    children: [
      {
        text: 'Lorem ipsum',
        code: true,
        bold: true,
        textColor: '#f60909',
        fillColor: '#f8e604',
      },
      {
        code: true,
        text: ' dolor sit amet',
      },
      {
        text: ', consectetur adipiscing elit.',
      },
    ],
  },
  {
    type: 'numbered-list',
    children: [
      {
        type: 'list-item',
        align: 'left',
        children: [
          {
            text: 'Suspendisse',
            code: true,
            bold: true,
          },
          {
            text: ' ',
          },
          {
            text: 'rutrum mollis urna eget hendrerit.',
            bold: true,
          },
        ],
      },
      {
        type: 'list-item',
        align: 'right',
        children: [
          {
            text: 'Integer risus felis, l',
          },
          {
            text: 'uctus egestas massa et, porta molestie quam.',
            italic: true,
          },
        ],
      },
      {
        type: 'list-item',
        align: 'left',
        children: [
          {
            text: 'Phasellus ultricies pharetra diam, ',
          },
          {
            text: 'quis dictum sapien dapibus eget',
            underline: true,
          },
        ],
      },
    ],
  },
];

export const RICH_TEXT_SAMPLE: IElement[] = [
  {
    type: 'paragraph',
    children: [
      {
        text: 'Scrum',
        bold: true,
        underline: true,
      },
      {
        text: ' ',
      },
      {
        text: 'stands out',
        italic: true,
      },
      {
        text: ' as the most dominant Agile framework with about 60% adoption rate across organizations globally. It is a proven way to transform businesses, catalyse innovation, and accelerate profitable growth. ',
      },
    ],
    align: 'left',
  },
  {
    type: 'heading-three',
    align: 'right',
    children: [
      {
        text: 'Leading organizations around the world including have adopted this powerful framework to improve time-to-market, boost quality, raise employee morale, and become truly adaptive organizations. ',
      },
    ],
  },
  {
    type: 'paragraph',
    align: 'left',
    children: [
      {
        text: 'With the ',
      },
      {
        text: 'World Economic Forum',
        textColor: '#ff0000',
        fillColor: '#b8e986',
        bold: true,
      },
      {
        text: ' naming the Scrum Master as one of the top three emerging job roles in Product Development (Jobs of Tomorrow 2020), this game-changing role for project delivery continues to be in high demand.',
      },
    ],
  },
  {
    type: 'bulleted-list',
    children: [
      {
        type: 'list-item',
        align: 'left',
        children: [
          {
            text: 'Spotify',
            code: true,
          },
        ],
      },
      {
        type: 'list-item',
        align: 'left',
        children: [
          {
            text: 'Twitter',
          },
        ],
      },
      {
        type: 'list-item',
        align: 'left',
        children: [
          {
            text: 'Tesla',
          },
        ],
      },
    ],
  },
];

const headingTagMappper: { [key: string]: string } = {
  [HeadingEnum.HEADING_ONE]: 'h1',
  [HeadingEnum.HEADING_TWO]: 'h2',
  [HeadingEnum.HEADING_THREE]: 'h3',
  [HeadingEnum.HEADING_FOUR]: 'h4',
  [HeadingEnum.HEADING_FIVE]: 'h5',
  [HeadingEnum.HEADING_SIX]: 'h6',
};

const headingStyleMappper: { [key: string]: IHeadingStyleMapper } = {
  [HeadingEnum.HEADING_ONE]: { fontSize: '32px', lineHeight: 1.3 },
  [HeadingEnum.HEADING_TWO]: { fontSize: '24px', lineHeight: 1.3 },
  [HeadingEnum.HEADING_THREE]: { fontSize: '18.72px', lineHeight: 1.3 },
  [HeadingEnum.HEADING_FOUR]: { fontSize: '16px', lineHeight: 1.3 },
  [HeadingEnum.HEADING_FIVE]: { fontSize: '13.28px', lineHeight: 1.3 },
  [HeadingEnum.HEADING_SIX]: { fontSize: '10.72px', lineHeight: 1.3 },
};

const convertStyleToString = (style: Record<string, string | number>) => {
  const styleStr = JSON.stringify(style)
    .replaceAll(/[\"\{\}]/g, '') // Remove symbols:  " { }
    .replaceAll(',', ';');

  return styleStr;
};

const getText = (children: ITextNode[]) => {
  const text = children.map((child) => {
    const {
      text: t = '',
      bold,
      underline,
      italic,
      code,
      textColor = '',
      fillColor = '',
      type,
      children,
      href,
    } = child;
    let text: string = t;
    if (textColor || fillColor) {
      text = `<span style="color:${textColor}; backgroundColor:${fillColor}">${text}</span>`;
    }

    if (underline) {
      text = `<u>${text}</u>`;
    }

    if (italic) {
      text = `<em>${text}</em>`;
    }

    if (bold) {
      text = `<strong>${text}</strong>`;
    }

    if (code) {
      text = `<code>${text}</code>`;
    }

    if (type === 'link') {
      const childText = children?.[0]?.text || '';
      text = `<a href=${href || '#'} target="_blank" rel="noreferrer">${childText}</a>`;
    }
    return text;
  });
  const result = text.join('');
  return result;
};

const getTextElement = ({
  children,
  align,
}: {
  children: ITextNode[];
  align: Align | undefined;
}) => {
  const text = getText(children);
  let style: Record<string, string | number> = {};

  if (align) {
    style['text-align'] = align;
  }

  // INFO: Empty paragraph
  const paragraphHeight = '12px';
  if (!text) {
    style.height = paragraphHeight;
  }

  const styleStr = convertStyleToString(style);
  return { styleStr, text };
};

export const richTextParser = (rawObject: IElement[] = []): string => {
  if (!Array.isArray(rawObject)) return rawObject;

  const result = rawObject?.map((child) => {
    const { type, children = [], align } = child;
    if (type === 'paragraph') {
      const { text, styleStr } = getTextElement({ children, align });
      return `<p style="${styleStr}">${text}</p>`;
    }

    if (type && type.includes('heading')) {
      const { text, styleStr } = getTextElement({ children, align });
      const tag = headingTagMappper[type];
      const fontSize = headingStyleMappper[type].fontSize;
      const lineHeight = headingStyleMappper[type].lineHeight;
      return `<${tag} style="${styleStr}; font-size: ${fontSize}; line-height: ${lineHeight}">${text}</${tag}>`;
    }

    if (type === 'bulleted-list' || type === 'numbered-list') {
      const items = children.map((item) => {
        const { type, align, children = [] } = item;

        if (type === 'list-item') {
          const { text, styleStr } = getTextElement({ children, align });
          // list-style-position: inside;
          return `<li style="${styleStr}">${text}</li>`;
        }
      });

      const itemElements = items.join('');

      if (type === 'bulleted-list') {
        return `<ul style="list-style-type: disc;">${itemElements}</ul>`;
      } else {
        return `<ol style="list-style-type: decimal;">${itemElements}</ol>`;
      }
    }
  });

  return result.join('');
};
